import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  dsrAuditsApi,
  SnapshotEntity,
  AuditQueueRecord,
  ArtistStatusLogEntry,
} from "../../../api/dsrAuditsApi";

interface AuditQueueArtist {
  name: string;
  normalizedName: string;
  record: AuditQueueRecord;
}

const formatAddedDate = (value: string | undefined): string => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatStatusAction = (entry: ArtistStatusLogEntry): string => {
  const from = entry.previousStatus ?? "new";
  return `${from} → ${entry.newStatus}`;
};

const queueStatusSeverity = (
  status: AuditQueueRecord["reviewStatus"] | undefined
): "success" | "danger" | "warning" | undefined => {
  if (status === "rejected") {
    return "danger";
  }
  if (status === "approved") {
    return "success";
  }
  return "warning";
};

const MasterArtistsRoute = () => {
  const toast = useRef<Toast>(null);
  const [artists, setArtists] = useState<SnapshotEntity[]>([]);
  const [history, setHistory] = useState<ArtistStatusLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueArtists, setQueueArtists] = useState<AuditQueueArtist[]>([]);
  const [approvingArtist, setApprovingArtist] = useState<string | null>(null);
  const [rejectingArtist, setRejectingArtist] = useState<string | null>(null);
  const [movingArtistId, setMovingArtistId] = useState<string | null>(null);
  const [exportingQueue, setExportingQueue] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const normalizeName = useCallback(
    (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase(),
    []
  );

  const acceptedArtists = useMemo(
    () => artists.filter((a) => a.status !== "rejected"),
    [artists]
  );

  const rejectedArtists = useMemo(
    () => artists.filter((a) => a.status === "rejected"),
    [artists]
  );

  const rejectedNormalized = useMemo(
    () => new Set(rejectedArtists.map((a) => a.normalizedName)),
    [rejectedArtists]
  );

  const pendingQueueArtists = useMemo(
    () =>
      queueArtists.filter((artist) => {
        const status = artist.record.reviewStatus;
        if (status && status !== "pending") {
          return false;
        }
        return !rejectedNormalized.has(artist.normalizedName);
      }),
    [queueArtists, rejectedNormalized]
  );

  const fetchArtists = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dsrAuditsApi.getArtistSnapshots();
      setArtists(response || []);
    } catch (error) {
      console.error("Error fetching master artists:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch master artists list",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await dsrAuditsApi.getArtistStatusHistory(200);
      setHistory(response || []);
    } catch (error) {
      console.error("Error fetching artist status history:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch change history",
        life: 3000,
      });
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const buildQueueArtists = useCallback(
    (records: AuditQueueRecord[]) => {
      const artistsFromQueue = new Map<string, AuditQueueArtist>();

      (records || []).forEach((record) => {
        if (record.reviewStatus === "approved") {
          return;
        }
        const artistIssue = record.unverifiedEntities?.find(
          (entity) => entity.entityType === "artist"
        );
        const artistName = artistIssue?.name || record.Artist;
        if (!artistName) {
          return;
        }

        const normalized = normalizeName(artistName);
        if (!artistsFromQueue.has(normalized)) {
          artistsFromQueue.set(normalized, {
            name: artistName,
            normalizedName: normalized,
            record,
          });
        }
      });

      return Array.from(artistsFromQueue.values());
    },
    [normalizeName]
  );

  const fetchAuditQueueArtists = useCallback(async () => {
    setQueueLoading(true);
    try {
      const response = await dsrAuditsApi.getLatestAuditQueue(
        "sound-recordings",
        { includeRejected: true }
      );
      setQueueArtists(buildQueueArtists(response || []));
    } catch (error) {
      console.error("Error fetching audit queue artists:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch audit queue artists",
        life: 3000,
      });
    } finally {
      setQueueLoading(false);
    }
  }, [buildQueueArtists]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchArtists(), fetchAuditQueueArtists(), fetchHistory()]);
  }, [fetchArtists, fetchAuditQueueArtists, fetchHistory]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const upsertArtistInState = (entity: SnapshotEntity) => {
    setArtists((prev) => {
      const without = prev.filter((a) => a.id !== entity.id);
      return [...without, entity].sort((a, b) =>
        a.normalizedName.localeCompare(b.normalizedName)
      );
    });
  };

  const addArtistSnapshot = async (name: string) => {
    const normalized = normalizeName(name);

    try {
      const created = await dsrAuditsApi.addArtistSnapshot(name);
      upsertArtistInState(created);
      setQueueArtists((prev) =>
        prev.filter((artist) => artist.normalizedName !== normalized)
      );
      return true;
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setQueueArtists((prev) =>
          prev.filter((artist) => artist.normalizedName !== normalized)
        );
        return false;
      }
      throw error;
    }
  };

  const handleApproveQueueArtist = async (artist: AuditQueueArtist) => {
    setApprovingArtist(artist.normalizedName);
    try {
      const wasCreated = await addArtistSnapshot(artist.name);
      if (artist.record.id) {
        await dsrAuditsApi.updateAuditQueueStatus(
          artist.record.id,
          "approved"
        );
      }
      setQueueArtists((prev) =>
        prev.filter((item) => item.normalizedName !== artist.normalizedName)
      );
      await fetchHistory();
      toast.current?.show({
        severity: wasCreated ? "success" : "info",
        summary: wasCreated ? "Accepted" : "Already accepted",
        detail: wasCreated
          ? "Artist added to trusted list"
          : "Artist is already on the trusted list",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to accept artist",
        life: 3000,
      });
    } finally {
      setApprovingArtist(null);
    }
  };

  const handleRejectQueueArtist = async (artist: AuditQueueArtist) => {
    if (!artist.record.id) {
      toast.current?.show({
        severity: "error",
        summary: "Missing Record",
        detail: "Unable to reject this item.",
        life: 3000,
      });
      return;
    }

    setRejectingArtist(artist.normalizedName);
    try {
      await dsrAuditsApi.updateAuditQueueStatus(artist.record.id, "rejected");
      setQueueArtists((prev) =>
        prev.filter((item) => item.normalizedName !== artist.normalizedName)
      );
      await fetchArtists();
      await fetchHistory();
      toast.current?.show({
        severity: "success",
        summary: "Rejected",
        detail: "Artist marked as rejected",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to reject artist",
        life: 3000,
      });
    } finally {
      setRejectingArtist(null);
    }
  };

  const handleSnapshotStatus = async (
    artist: SnapshotEntity,
    status: "accepted" | "rejected"
  ) => {
    setMovingArtistId(artist.id);
    try {
      const updated = await dsrAuditsApi.updateArtistSnapshotStatus(
        artist.id,
        status
      );
      upsertArtistInState(updated);
      await fetchHistory();
      toast.current?.show({
        severity: "success",
        summary: status === "accepted" ? "Accepted" : "Rejected",
        detail:
          status === "accepted"
            ? "Artist moved to trusted list"
            : "Artist moved to rejected list",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update artist status",
        life: 3000,
      });
    } finally {
      setMovingArtistId(null);
    }
  };

  const confirmRejectTrusted = (artist: SnapshotEntity) => {
    confirmDialog({
      message: `Move "${artist.name}" to the rejected list? Future Sound Recordings reports will treat this artist as unverified.`,
      header: "Reject trusted artist",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Reject",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: () => handleSnapshotStatus(artist, "rejected"),
    });
  };

  const handleExportQueue = async () => {
    setExportingQueue(true);
    try {
      const response = await dsrAuditsApi.getLatestAuditQueue(
        "sound-recordings",
        { includeRejected: true }
      );
      const exportRows = buildQueueArtists(response || []);
      const headers = ["Artist", "SongTitle", "AssetID", "ISRC", "Status"];
      const rows = exportRows.map((row) => [
        row.name,
        row.record.SongTitle || "",
        row.record.AssetID || "",
        row.record.ISRC || "",
        row.record.reviewStatus || "pending",
      ]);

      const escapeCsv = (value: string) =>
        `"${String(value).replace(/"/g, '""')}"`;
      const csvContent = [
        headers.map(escapeCsv).join(","),
        ...rows.map((row) => row.map(escapeCsv).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `master_artists_audit_queue_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to export audit queue",
        life: 3000,
      });
    } finally {
      setExportingQueue(false);
    }
  };

  const summaryChip = (
    label: string,
    count: number,
    severity: "success" | "danger" | "warning"
  ) => (
    <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-summary-card px-4 py-2">
      <Tag value={label} severity={severity} />
      <span className="text-lg font-semibold p-summary-card-content">{count}</span>
    </div>
  );

  const queueStatusBody = (row: AuditQueueArtist) => {
    const status = row.record.reviewStatus || "pending";
    return <Tag value={status} severity={queueStatusSeverity(status)} />;
  };

  const queueActionsBody = (row: AuditQueueArtist) => {
    const isRejected = row.record.reviewStatus === "rejected";
    return (
      <div className="flex items-center gap-2">
        <Button
          label="Accept"
          icon="pi pi-check"
          className="p-button-sm"
          severity="success"
          loading={approvingArtist === row.normalizedName}
          onClick={() => handleApproveQueueArtist(row)}
        />
        {!isRejected && (
          <Button
            label="Reject"
            icon="pi pi-times"
            className="p-button-sm p-button-danger"
            loading={rejectingArtist === row.normalizedName}
            onClick={() => handleRejectQueueArtist(row)}
          />
        )}
      </div>
    );
  };

  const trustedActionsBody = (row: SnapshotEntity) => (
    <Button
      label="Reject"
      icon="pi pi-times"
      className="p-button-sm p-button-danger"
      loading={movingArtistId === row.id}
      onClick={() => confirmRejectTrusted(row)}
    />
  );

  const rejectedActionsBody = (row: SnapshotEntity) => (
    <Button
      label="Accept"
      icon="pi pi-check"
      className="p-button-sm"
      severity="success"
      loading={movingArtistId === row.id}
      onClick={() => handleSnapshotStatus(row, "accepted")}
    />
  );

  const renderQueueTable = (rows: AuditQueueArtist[], emptyMessage: string) => (
    <DataTable
      value={rows}
      paginator
      rows={10}
      rowsPerPageOptions={[5, 10, 25, 50]}
      className="p-datatable-sm"
      emptyMessage={emptyMessage}
    >
      <Column field="name" header="Artist" sortable />
      <Column
        header="Song Title"
        body={(row: AuditQueueArtist) => row.record.SongTitle || "—"}
        style={{ minWidth: "180px" }}
      />
      <Column
        header="Asset ID"
        body={(row: AuditQueueArtist) => row.record.AssetID || "—"}
        style={{ minWidth: "160px" }}
      />
      <Column
        header="ISRC"
        body={(row: AuditQueueArtist) => row.record.ISRC || "—"}
        style={{ minWidth: "140px" }}
      />
      <Column header="Status" body={queueStatusBody} style={{ minWidth: "120px" }} />
      <Column header="Actions" body={queueActionsBody} style={{ minWidth: "180px" }} />
    </DataTable>
  );

  const renderSnapshotTable = (
    rows: SnapshotEntity[],
    emptyMessage: string,
    actionsBody: (row: SnapshotEntity) => JSX.Element,
    dateField: "createdAt" | "statusUpdatedAt",
    byField: "createdBy" | "statusUpdatedBy"
  ) => (
    <DataTable
      value={rows}
      paginator
      rows={10}
      rowsPerPageOptions={[5, 10, 25, 50]}
      className="p-datatable-sm"
      emptyMessage={emptyMessage}
    >
      <Column field="name" header="Artist Name" sortable />
      <Column field="normalizedName" header="Normalized" sortable />
      <Column
        field={dateField}
        header={dateField === "createdAt" ? "Added" : "Updated"}
        sortable
        body={(row: SnapshotEntity) => formatAddedDate(row[dateField])}
      />
      <Column
        field={byField}
        header={byField === "createdBy" ? "Added By" : "Updated By"}
        body={(row: SnapshotEntity) => row[byField] || "—"}
      />
      <Column header="Actions" body={actionsBody} style={{ minWidth: "120px" }} />
    </DataTable>
  );

  return (
    <div className="space-y-6">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="app-menubar-title rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-3xl font-bold app-menubar-title mb-2">Master Artists</h1>
        <p className="p-subtitle text-lg break-normal whitespace-normal">
          Manage trusted and rejected artists used for Sound Recordings
          report validation. Changes are logged for audit.
        </p>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex flex-wrap gap-3">
            {summaryChip("Trusted", acceptedArtists.length, "success")}
            {summaryChip("Rejected", rejectedArtists.length, "danger")}
            {summaryChip("Needs review", pendingQueueArtists.length, "warning")}
          </div>
          <div className="flex items-center gap-2">
            <Button
              label="Refresh"
              icon="pi pi-refresh"
              className="p-button-sm"
              severity="info"
              style={{ height: "32px" }}
              loading={loading || queueLoading || historyLoading}
              onClick={refreshAll}
            />
            <Button
              label="Export queue CSV"
              icon="pi pi-download"
              className="p-button-sm"
              severity="info"
              style={{ height: "32px" }}
              loading={exportingQueue}
              onClick={handleExportQueue}
            />
          </div>
        </div>

        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          <TabPanel header={`Trusted (${acceptedArtists.length})`}>
            {loading ? (
              <div className="flex justify-center items-center h-40">
                <ProgressSpinner />
              </div>
            ) : (
              renderSnapshotTable(
                acceptedArtists,
                "No trusted artists yet",
                trustedActionsBody,
                "createdAt",
                "createdBy"
              )
            )}
          </TabPanel>

          <TabPanel header={`Rejected (${rejectedArtists.length})`}>
            {loading ? (
              <div className="flex justify-center items-center h-40">
                <ProgressSpinner />
              </div>
            ) : (
              renderSnapshotTable(
                rejectedArtists,
                "No rejected artists",
                rejectedActionsBody,
                "statusUpdatedAt",
                "statusUpdatedBy"
              )
            )}
          </TabPanel>

          <TabPanel header={`Needs review (${pendingQueueArtists.length})`}>
            <p className="text-sm text-gray-600 mb-3">
              Artists from the latest Sound Recordings report run that are not
              on the trusted list.
            </p>
            {queueLoading ? (
              <div className="flex justify-center items-center h-40">
                <ProgressSpinner />
              </div>
            ) : (
              renderQueueTable(
                pendingQueueArtists,
                "No artists need review for the latest report run"
              )
            )}
          </TabPanel>

          <TabPanel header="Change history">
            {historyLoading ? (
              <div className="flex justify-center items-center h-40">
                <ProgressSpinner />
              </div>
            ) : (
              <DataTable
                value={history}
                paginator
                rows={15}
                rowsPerPageOptions={[10, 15, 25, 50]}
                className="p-datatable-sm"
                emptyMessage="No status changes recorded yet"
              >
                <Column field="artistName" header="Artist" sortable />
                <Column
                  header="Action"
                  body={(row: ArtistStatusLogEntry) => formatStatusAction(row)}
                  style={{ minWidth: "160px" }}
                />
                <Column field="changedBy" header="User" sortable />
                <Column
                  field="changedAt"
                  header="When"
                  sortable
                  body={(row: ArtistStatusLogEntry) =>
                    formatAddedDate(row.changedAt)
                  }
                />
                <Column
                  field="source"
                  header="Source"
                  body={(row: ArtistStatusLogEntry) =>
                    row.source === "audit_queue" ? "Audit queue" : "Master list"
                  }
                />
              </DataTable>
            )}
          </TabPanel>
        </TabView>
      </Card>
    </div>
  );
};

export default MasterArtistsRoute;
